import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, ShieldAlert, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { useAuthStore } from "@/store/auth";
import {
  useAddConnectionCareFlag,
  useAddConnectionCareNote,
  useCareAffected,
  useCareApply,
  useCareEntryMutations,
  useConnectionCare,
} from "../hooks";
import type {
  CareArrangementBrief,
  CareFlagBrief,
  CareSummary,
  PowerProject,
} from "../types";

/**
 * Zakładka „Opieka" — pełna karta specyfiki oddziału klienta prowadzona z poziomu
 * projektu ZE (moduł „trudnego klienta"). Trzy filary:
 *
 *  * FLAGI  — ostrzegawcze etykiety zasilające badge „⚠ OPIEKA" (workspace, portfel,
 *             selektor oddziału);
 *  * USTALENIA — ostrzeżenie (komunikat w projektach) albo WYMAGANY KROK: kamień
 *             „Dotyczy" dokładany automatycznie każdemu projektowi oddziału/pododdziału
 *             (nakładka na szablon procesu — reset szablonu jej nie kasuje);
 *  * DZIENNIK — chronologia zdarzeń z autorem i linkiem do projektu.
 *
 * Wpis zawsze wisi na POZIOMIE (klient / oddział / pododdział), nie na projekcie —
 * dodany tutaj obowiązuje wszystkie projekty tego poziomu. Zarządzają: CEO, dyrektor
 * techniczny, koordynatorzy; dziennik pisze każdy. Dane wyłącznie wewnętrzne.
 */

const SCOPE_LABEL: Record<CareFlagBrief["scope"], string> = {
  client: "klient",
  branch: "oddział",
  sub_branch: "pododdział",
};

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString("pl-PL");

type ScopeKey = "sub_branch" | "branch" | "client";

function scopeOptions(
  connection: PowerProject,
  scope: CareSummary["scope"],
  withClient: boolean,
) {
  const out: { key: ScopeKey; id: string; label: string }[] = [];
  if (scope.sub_branch)
    out.push({
      key: "sub_branch",
      id: scope.sub_branch,
      label: `Pododdział: ${connection.sub_branch_name ?? ""}`,
    });
  if (scope.branch)
    out.push({
      key: "branch",
      id: scope.branch,
      label: `Oddział: ${connection.branch_name ?? ""}`,
    });
  if (withClient && scope.client)
    out.push({
      key: "client",
      id: scope.client,
      label: `Klient: ${connection.client_info?.name ?? ""}`,
    });
  return out;
}

/** Karta-sekcja: nagłówek z licznikiem i akcją, ciało z listą. */
function SectionCard({
  label,
  count,
  action,
  children,
}: {
  label: string;
  count?: number;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="card border-[var(--color-border-strong)]">
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-[var(--color-border-strong)]">
        <span className="font-mono text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-primary)]">
          {label}
          {typeof count === "number" && count > 0 && (
            <span className="ml-1.5 font-normal text-[var(--color-text-muted)]">
              · {count}
            </span>
          )}
        </span>
        {action}
      </div>
      <div className="p-4 flex flex-col gap-2">{children}</div>
    </section>
  );
}

function EntryRow({
  accent,
  dimmed,
  children,
  meta,
  actions,
}: {
  accent: "amber" | "neutral";
  dimmed?: boolean;
  children: React.ReactNode;
  meta: React.ReactNode;
  actions?: React.ReactNode;
}) {
  return (
    <li
      className={`card px-3 py-2 ${dimmed ? "opacity-55" : ""}`}
      style={{
        borderLeftWidth: 3,
        borderLeftColor:
          accent === "amber" ? "#d97706" : "var(--color-border-strong)",
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">{children}</div>
        {actions && (
          <div className="flex shrink-0 items-center gap-1">{actions}</div>
        )}
      </div>
      <p className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mt-1">
        {meta}
      </p>
    </li>
  );
}

const xsBtn =
  "border border-[var(--color-border-strong)] px-2 py-1 text-[11px] text-[var(--color-text-secondary)] hover:border-[var(--color-text-primary)] hover:text-[var(--color-text-primary)] transition-colors";

/** „Dodaj kamień do trwających projektów" — podgląd + potwierdzenie. */
function ApplyPanel({
  arrangementId,
  onDone,
}: {
  arrangementId: string;
  onDone: () => void;
}) {
  const { data, isLoading } = useCareAffected(arrangementId);
  const apply = useCareApply();
  const [checked, setChecked] = useState<Record<string, boolean> | null>(null);
  const [result, setResult] = useState<number | null>(null);

  if (isLoading)
    return (
      <p className="text-xs text-[var(--color-text-muted)] mt-2">
        Sprawdzam trwające projekty…
      </p>
    );
  const projects = data?.projects ?? [];
  if (result !== null) {
    return (
      <p className="text-xs text-[var(--color-text-secondary)] mt-2">
        Dodano kamień do {result} projekt(ów).{" "}
        <button className="underline" onClick={onDone}>
          OK
        </button>
      </p>
    );
  }
  if (projects.length === 0) {
    return (
      <p className="text-xs text-[var(--color-text-muted)] mt-2">
        Wszystkie trwające projekty mają już ten kamień; nowe dostaną go
        automatycznie.{" "}
        <button className="underline" onClick={onDone}>
          OK
        </button>
      </p>
    );
  }
  const state =
    checked ?? Object.fromEntries(projects.map((p) => [p.connection_id, true]));
  const selected = projects
    .filter((p) => state[p.connection_id])
    .map((p) => p.connection_id);
  return (
    <div className="mt-2 border border-amber-500 bg-amber-500/5 p-3">
      <p className="text-xs font-medium text-[var(--color-text-primary)] mb-2">
        Dodać kamień „{data?.arrangement.step_label}” do trwających projektów?
        (nowe dostają go automatycznie)
      </p>
      <div className="max-h-40 overflow-y-auto space-y-1 mb-2.5">
        {projects.map((p) => (
          <label
            key={p.connection_id}
            className="flex items-center gap-2 text-xs text-[var(--color-text-secondary)]"
          >
            <input
              type="checkbox"
              checked={!!state[p.connection_id]}
              onChange={(e) =>
                setChecked({ ...state, [p.connection_id]: e.target.checked })
              }
            />
            {p.project_name}
          </label>
        ))}
      </div>
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={selected.length === 0}
          loading={apply.isPending}
          onClick={() =>
            apply.mutate(
              { id: arrangementId, connections: selected },
              { onSuccess: (r) => setResult(r.count) },
            )
          }
        >
          Dodaj kamień ({selected.length})
        </Button>
        <Button variant="ghost" size="sm" onClick={onDone}>
          Pomiń
        </Button>
      </div>
    </div>
  );
}

function AddFlagForm({
  connection,
  scope,
  onDone,
}: {
  connection: PowerProject;
  scope: CareSummary["scope"];
  onDone: () => void;
}) {
  const options = scopeOptions(connection, scope, true);
  const addFlag = useAddConnectionCareFlag(connection.id);
  const [label, setLabel] = useState("");
  const [reason, setReason] = useState("");
  const [level, setLevel] = useState<ScopeKey>(options[0]?.key ?? "client");
  if (options.length === 0) return null;
  const submit = () => {
    const target = options.find((o) => o.key === level) ?? options[0];
    if (!label.trim() || !target) return;
    addFlag.mutate(
      { [target.key]: target.id, label: label.trim(), reason: reason.trim() },
      { onSuccess: onDone },
    );
  };
  return (
    <div className="card border-[var(--color-border-strong)] p-3 flex flex-col gap-2">
      <input
        autoFocus
        className="input-base"
        placeholder="Etykieta, np. „Wymagający przy koncepcji”"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
      />
      <input
        className="input-base"
        placeholder="Powód / czego pilnować — pokaże się w dymku (opcjonalnie)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <div className="flex flex-wrap items-center gap-2">
        <select
          className="input-base w-auto"
          value={level}
          onChange={(e) => setLevel(e.target.value as ScopeKey)}
          title="Flaga obejmie wszystkie projekty wybranego poziomu"
        >
          {options.map((o) => (
            <option key={o.key} value={o.key}>
              {o.label}
            </option>
          ))}
        </select>
        <span className="flex-1" />
        <Button variant="ghost" size="sm" onClick={onDone}>
          Anuluj
        </Button>
        <Button
          size="sm"
          disabled={!label.trim()}
          loading={addFlag.isPending}
          onClick={submit}
        >
          Dodaj flagę
        </Button>
      </div>
    </div>
  );
}

function AddArrangementForm({
  connection,
  scope,
  onCreated,
  onDone,
}: {
  connection: PowerProject;
  scope: CareSummary["scope"];
  onCreated: (arr: { id: string; kind: string }) => void;
  onDone: () => void;
}) {
  const options = scopeOptions(connection, scope, false); // ustalenia: tylko oddział/pododdział
  const { createArrangement } = useCareEntryMutations(connection.id);
  const [kind, setKind] = useState<"warning" | "required_step">(
    "required_step",
  );
  const [stepLabel, setStepLabel] = useState("");
  const [text, setText] = useState("");
  // SLA kroku w dniach: faza 1 = NASZA akcja (złożenie/przygotowanie), faza 2 =
  // odpowiedź strony. Puste = kamień bez terminu (zachowanie sprzed tej zmiany).
  const [submitSla, setSubmitSla] = useState("");
  const [resolveSla, setResolveSla] = useState("");
  const [level, setLevel] = useState<ScopeKey>(options[0]?.key ?? "branch");
  if (options.length === 0) {
    return (
      <p className="text-xs text-[var(--color-text-muted)]">
        Projekt nie jest przypięty do oddziału — ustaw oddział w zakładce
        Metadane, żeby dodawać ustalenia.
      </p>
    );
  }
  const valid = kind === "warning" ? !!text.trim() : !!stepLabel.trim();
  const submit = () => {
    const target = options.find((o) => o.key === level) ?? options[0];
    if (!valid || !target) return;
    const days = (v: string) =>
      v.trim() === "" ? null : Math.max(0, parseInt(v, 10) || 0);
    createArrangement.mutate(
      {
        [target.key]: target.id,
        kind,
        text: text.trim(),
        step_label: stepLabel.trim(),
        submit_sla_days: kind === "required_step" ? days(submitSla) : null,
        resolve_sla_days: kind === "required_step" ? days(resolveSla) : null,
      },
      {
        onSuccess: (created) => {
          onDone();
          onCreated(created);
        },
      },
    );
  };
  return (
    <div className="card border-[var(--color-border-strong)] p-3 flex flex-col gap-2">
      <div className="flex flex-wrap gap-2">
        <select
          className="input-base w-auto"
          value={kind}
          onChange={(e) =>
            setKind(e.target.value as "warning" | "required_step")
          }
        >
          <option value="required_step">Wymagany krok (kamień)</option>
          <option value="warning">Ostrzeżenie</option>
        </select>
        <select
          className="input-base w-auto flex-1 min-w-[160px]"
          value={level}
          onChange={(e) => setLevel(e.target.value as ScopeKey)}
          title="Ustalenie obejmie wszystkie projekty wybranego poziomu"
        >
          {options.map((o) => (
            <option key={o.key} value={o.key}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
      {kind === "required_step" && (
        <>
          <input
            autoFocus
            className="input-base"
            placeholder="Nazwa kamienia, np. „SCHEMAT IDEOWY”"
            value={stepLabel}
            onChange={(e) => setStepLabel(e.target.value)}
          />
          {/* SLA rozbite tak jak w szablonie procesu: nasza faza i faza strony
              liczone osobno — z tego biorą się prognozy i ostrzeżenia o terminach. */}
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1">
              <span className="section-label">SLA — my (dni)</span>
              <input
                type="number"
                min={0}
                className="input-base w-28"
                placeholder="np. 14"
                value={submitSla}
                onChange={(e) => setSubmitSla(e.target.value)}
                title="Ile dni mamy na naszą część kroku (przygotowanie / złożenie)"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="section-label">SLA — oni (dni)</span>
              <input
                type="number"
                min={0}
                className="input-base w-28"
                placeholder="np. 30"
                value={resolveSla}
                onChange={(e) => setResolveSla(e.target.value)}
                title="Ile dni strona ma na odpowiedź / uzgodnienie"
              />
            </label>
            <span className="flex-1 min-w-[180px] text-[11px] text-[var(--color-text-muted)]">
              Puste = kamień bez terminu.
            </span>
          </div>
        </>
      )}
      <input
        className="input-base"
        placeholder={
          kind === "warning"
            ? "Treść ostrzeżenia widoczna w projektach tego poziomu"
            : "Opis (opcjonalnie): skąd wymóg, czego pilnować"
        }
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      {kind === "required_step" && (
        <p className="text-[11px] text-[var(--color-text-muted)]">
          Każdy NOWY projekt tego poziomu dostanie kamień ze statusem „Dotyczy”
          automatycznie; o dołożeniu do trwających zdecydujesz za chwilę na
          liście z podglądem.
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onDone}>
          Anuluj
        </Button>
        <Button
          size="sm"
          disabled={!valid}
          loading={createArrangement.isPending}
          onClick={submit}
        >
          Dodaj ustalenie
        </Button>
      </div>
    </div>
  );
}

export function CareTab({ connection }: { connection: PowerProject }) {
  const user = useAuthStore((s) => s.user);
  const { data, isLoading } = useConnectionCare(connection.id);
  const addNote = useAddConnectionCareNote(connection.id);
  const {
    updateFlag,
    removeFlag,
    updateArrangement,
    removeArrangement,
    removeNote,
  } = useCareEntryMutations(connection.id);
  const [noteText, setNoteText] = useState("");
  const [addingFlag, setAddingFlag] = useState(false);
  const [addingArrangement, setAddingArrangement] = useState(false);
  const [applyingId, setApplyingId] = useState<string | null>(null);

  if (isLoading || !data) {
    return (
      <p className="p-4 text-sm text-[var(--color-text-muted)]">
        Wczytywanie karty specyfiki…
      </p>
    );
  }

  const canManage = data.can_manage;
  const activeFlags = data.flags.filter((f) => f.is_active !== false);
  const activeSteps = data.arrangements.filter(
    (a) => a.is_active && a.kind === "required_step",
  );
  const scopePath =
    [
      connection.client_info?.name,
      connection.branch_name,
      connection.sub_branch_name,
    ]
      .filter(Boolean)
      .join("  →  ") || "—";

  const submitNote = () => {
    if (!noteText.trim()) return;
    addNote.mutate(noteText.trim(), { onSuccess: () => setNoteText("") });
  };

  const arrangementRow = (a: CareArrangementBrief) => (
    <EntryRow
      key={a.id}
      accent={a.kind === "required_step" ? "amber" : "neutral"}
      dimmed={!a.is_active}
      meta={
        <>
          {SCOPE_LABEL[a.scope]} · {a.scope_name}
          {!a.is_active && " · wyłączone"}
        </>
      }
      actions={
        canManage ? (
          <>
            {a.kind === "required_step" && a.is_active && (
              <button
                className={xsBtn}
                onClick={() => setApplyingId(applyingId === a.id ? null : a.id)}
              >
                Dodaj do trwających…
              </button>
            )}
            <button
              className={xsBtn}
              onClick={() =>
                updateArrangement.mutate({ id: a.id, is_active: !a.is_active })
              }
            >
              {a.is_active ? "Wyłącz" : "Włącz"}
            </button>
            <button
              className={xsBtn}
              title="Usuń ustalenie (kamienie już dodane do projektów zostają)"
              onClick={() => removeArrangement.mutate(a.id)}
            >
              <Trash2 size={12} />
            </button>
          </>
        ) : undefined
      }
    >
      <span
        className={`inline-block font-mono text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 border mr-2 ${
          a.kind === "required_step"
            ? "border-amber-500 text-amber-700 dark:text-amber-400"
            : "border-[var(--color-border-strong)] text-[var(--color-text-secondary)]"
        }`}
      >
        {a.kind === "required_step" ? "Kamień" : "Ostrzeżenie"}
      </span>
      {a.kind === "required_step" && (
        <>
          <span className="text-sm font-semibold text-[var(--color-text-primary)]">
            {a.step_label}
          </span>
          {(a.submit_sla_days != null || a.resolve_sla_days != null) && (
            <span
              className="ml-2 font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]"
              title="SLA kroku: nasza faza / odpowiedź strony"
            >
              SLA: my {a.submit_sla_days ?? "—"} d · oni {a.resolve_sla_days ?? "—"} d
            </span>
          )}
        </>
      )}
      {a.text && (
        <p className="text-sm text-[var(--color-text-secondary)] whitespace-pre-wrap mt-0.5">
          {a.text}
        </p>
      )}
      {applyingId === a.id && (
        <ApplyPanel arrangementId={a.id} onDone={() => setApplyingId(null)} />
      )}
    </EntryRow>
  );

  // Sekcja w zakładce „Metadane" (nie osobna zakładka): jeden pasek z poziomem karty
  // i licznikami, pod nim trzy kolumny — flagi, ustalenia, dziennik.
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center border border-amber-500 bg-amber-500/10 text-amber-600">
          <ShieldAlert size={14} />
        </span>
        <p
          className="min-w-0 font-mono text-[11px] uppercase tracking-wider text-[var(--color-text-muted)] truncate"
          title={scopePath}
        >
          {scopePath}
        </p>
        <div className="ml-auto flex items-center gap-4 font-mono text-[11px] uppercase tracking-wider text-[var(--color-text-muted)]">
          <span title="Flagi szczególnej opieki (aktywne)">
            Flagi{" "}
            <b className={activeFlags.length ? "text-amber-600" : "text-[var(--color-text-primary)]"}>
              {activeFlags.length}
            </b>
          </span>
          <span title="Kamienie z ustaleń (wymagane kroki)">
            Ustalenia{" "}
            <b className={activeSteps.length ? "text-amber-600" : "text-[var(--color-text-primary)]"}>
              {activeSteps.length}
            </b>
          </span>
          <span title="Wpisy w dzienniku obserwacji">
            Dziennik <b className="text-[var(--color-text-primary)]">{data.notes.length}</b>
          </span>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3 items-start">
          {/* ── FLAGI ─────────────────────────────────────────────────── */}
          <SectionCard
            label="Flagi szczególnej opieki"
            count={data.flags.length}
            action={
              canManage && !addingFlag ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setAddingFlag(true)}
                >
                  <span className="inline-flex items-center gap-1">
                    <Plus size={12} /> Flaga
                  </span>
                </Button>
              ) : undefined
            }
          >
            {data.flags.length === 0 && !addingFlag && (
              <p className="text-sm text-[var(--color-text-muted)]">
                Brak flag. Flaga („Wymagający przy koncepcji”, „Długi czas
                odpowiedzi”…) zapala badge ⚠ przy nazwie KAŻDEGO projektu tego
                poziomu — w workspace, portfelu i przy wyborze oddziału.
              </p>
            )}
            <ul className="space-y-2">
              {data.flags.map((f) => (
                <EntryRow
                  key={f.id}
                  accent="amber"
                  dimmed={f.is_active === false}
                  meta={
                    <>
                      {SCOPE_LABEL[f.scope]} · {f.scope_name}
                      {f.created_by_name && <> · {f.created_by_name}</>}
                      {f.is_active === false && " · wygaszona"}
                    </>
                  }
                  actions={
                    canManage ? (
                      <>
                        <button
                          className={xsBtn}
                          onClick={() =>
                            updateFlag.mutate({
                              id: f.id,
                              is_active: f.is_active === false,
                            })
                          }
                        >
                          {f.is_active === false ? "Przywróć" : "Wygaś"}
                        </button>
                        <button
                          className={xsBtn}
                          title="Usuń flagę"
                          onClick={() => removeFlag.mutate(f.id)}
                        >
                          <Trash2 size={12} />
                        </button>
                      </>
                    ) : undefined
                  }
                >
                  <p className="text-sm font-semibold text-[var(--color-text-primary)]">
                    {f.label}
                  </p>
                  {f.reason && (
                    <p className="text-sm text-[var(--color-text-secondary)] whitespace-pre-wrap mt-0.5">
                      {f.reason}
                    </p>
                  )}
                </EntryRow>
              ))}
            </ul>
            {addingFlag && (
              <AddFlagForm
                connection={connection}
                scope={data.scope}
                onDone={() => setAddingFlag(false)}
              />
            )}
          </SectionCard>

          {/* ── USTALENIA ─────────────────────────────────────────────── */}
          <SectionCard
            label="Ustalenia specjalne"
            count={data.arrangements.length}
            action={
              canManage && !addingArrangement ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setAddingArrangement(true)}
                >
                  <span className="inline-flex items-center gap-1">
                    <Plus size={12} /> Ustalenie
                  </span>
                </Button>
              ) : undefined
            }
          >
            {data.arrangements.length === 0 && !addingArrangement && (
              <p className="text-sm text-[var(--color-text-muted)]">
                Brak ustaleń. <strong>Wymagany krok</strong> dodaje kamień
                „Dotyczy” każdemu projektowi oddziału/pododdziału (np. „WYMAGANY
                SCHEMAT IDEOWY”) — niezależnie od szablonu procesu.
                <strong> Ostrzeżenie</strong> to komunikat pokazywany w
                projektach.
              </p>
            )}
            <ul className="space-y-2">
              {data.arrangements.map(arrangementRow)}
            </ul>
            {addingArrangement && (
              <AddArrangementForm
                connection={connection}
                scope={data.scope}
                onDone={() => setAddingArrangement(false)}
                onCreated={(arr) => {
                  if (arr.kind === "required_step") setApplyingId(arr.id);
                }}
              />
            )}
          </SectionCard>

        {/* ── DZIENNIK ──────────────────────────────────────────────── */}
        <SectionCard label="Dziennik obserwacji" count={data.notes.length}>
          <textarea
            className="input-base min-h-[64px] resize-y"
            placeholder="Co się wydarzyło? (np. „ponownie uwagi do koncepcji mimo naniesionych poprawek”) — wpis zobaczą wszyscy prowadzący tematy tego oddziału."
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
          />
          <div className="flex justify-end">
            <Button
              size="sm"
              disabled={!noteText.trim()}
              loading={addNote.isPending}
              onClick={submitNote}
            >
              Dopisz do dziennika
            </Button>
          </div>
          {data.notes.length === 0 ? (
            <p className="text-sm text-[var(--color-text-muted)]">
              Brak wpisów — tu buduje się historia współpracy, którą zobaczy
              każda następna osoba na temacie.
            </p>
          ) : (
            <ul className="space-y-2">
              {data.notes.map((n) => (
                <EntryRow
                  key={n.id}
                  accent="neutral"
                  meta={
                    <>
                      {/* Poziom (klient / oddział / pododdział) i projekt, przy którym
                          wpis powstał — bez tego nie wiadomo, czego obserwacja dotyczy. */}
                      <span className="border border-[var(--color-border-strong)] px-1 py-0.5">
                        {n.scope_label || "—"}
                      </span>{" "}
                      ·{" "}
                      {n.project && n.project_name ? (
                        n.project === connection.project ? (
                          <span title="Wpis dodany z tego projektu">
                            projekt: {n.project_name}
                          </span>
                        ) : (
                          <Link
                            to={`/power-projects/${n.project}`}
                            className="underline normal-case hover:text-[var(--color-primary)]"
                            title="Wpis dodany przy innym projekcie tego poziomu — otwórz"
                          >
                            projekt: {n.project_name}
                          </Link>
                        )
                      ) : (
                        <span title="Wpis dodany poza kontekstem projektu">
                          bez projektu
                        </span>
                      )}{" "}
                      · {n.author_name ?? "—"} · {fmtDate(n.created_at)}
                    </>
                  }
                  actions={
                    canManage || (user && n.author === user.id) ? (
                      <button
                        className={xsBtn}
                        title="Usuń wpis"
                        onClick={() => removeNote.mutate(n.id)}
                      >
                        <Trash2 size={12} />
                      </button>
                    ) : undefined
                  }
                >
                  <p className="text-sm text-[var(--color-text-primary)] whitespace-pre-wrap">
                    {n.text}
                  </p>
                </EntryRow>
              ))}
            </ul>
          )}
        </SectionCard>
      </div>

      <p className="text-[11px] text-[var(--color-text-muted)]">
        Dane wewnętrzne GK ELEKTIM — niewidoczne dla kont podwykonawców, nie
        trafiają do generowanych dokumentów. Opisuj wymagania i zdarzenia, nie
        osoby. Wpisy obowiązują CAŁY poziom (klient / oddział / pododdział), nie
        pojedynczy projekt.
      </p>
    </div>
  );
}
